import React, { useCallback, useEffect, useRef, useState } from "react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, X, Loader2, CheckCircle2, XCircle, Clock3 } from "lucide-react";
import {
  triggerWorkflow, getActiveRun, getRun, getCompany, cancelRun,
  uploadBatch, getActiveBatch, getBatch, getBatchCompanies,
} from "./api";
import { CompanyResultView } from "./CompanyResultView";
import { StatusStepper } from "./StatusStepper";
import { formatDuration } from "./utils";

// How often to re-check an in-progress run/batch's status while waiting for
// n8n to call back.
const POLL_INTERVAL_MS = 4000;

// Real full-pipeline runs measured via n8n's own execution history land in
// this range (one clocked at 34.2s, another at 45s) - used only to frame
// the elapsed-time display ("usually finishes in ~30-35s"), never as a
// literal countdown, since a specific run can legitimately run longer.
const ESTIMATED_SECONDS_PER_COMPANY = 32;

function Startworlflow({ onRunFinished } = {}) {
  const [mode, setMode] = useState("single"); // "single" | "batch"

  // --- Single-company trigger form state ---
  const [name, setName] = useState("");
  const [companyUrl, setCompanyUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activeRun, setActiveRun] = useState(null); // null = nothing in progress
  const [checkingRunLock, setCheckingRunLock] = useState(true);
  const [cancellingRun, setCancellingRun] = useState(false);
  const runPollRef = useRef(null);

  // --- Result view (shown under the single-company form once it finishes) ---
  const [resultCompany, setResultCompany] = useState(null);
  const [loadingResult, setLoadingResult] = useState(false);

  // --- Live progress while a single run is still going ---
  // The backend only knows which `lead_engine` row a run belongs to once
  // n8n's Stage-1 callback reports it (POST /runs/{id}/link-company, fired
  // right after that row is INSERTed - well before the workflow finishes) -
  // until then run.company_id is null and there's nothing to poll a status
  // from, so the banner just says "in progress" with no stepper yet.
  const [liveStatus, setLiveStatus] = useState(null);
  const [runElapsedSec, setRunElapsedSec] = useState(0);

  // --- CSV bulk-upload state ---
  const [csvFile, setCsvFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadingBatch, setUploadingBatch] = useState(false);
  const [activeBatch, setActiveBatch] = useState(null); // null = no batch in progress
  const [checkingBatchLock, setCheckingBatchLock] = useState(true);
  const batchPollRef = useRef(null);
  const [batchElapsedSec, setBatchElapsedSec] = useState(0);
  const [batchCompanies, setBatchCompanies] = useState([]);
  const [csvErrorDialog, setCsvErrorDialog] = useState(null); // string | null - shown verbatim per the ask
  const fileInputRef = useRef(null);

  const isRunLocked = activeRun?.status === "in_progress";
  const isBatchLocked = activeBatch?.status === "in_progress";
  // Backend 409s a single trigger if a batch is active and vice versa, so
  // both forms stay disabled regardless of which one is actually running.
  const isAnyLocked = isRunLocked || isBatchLocked;

  // ---------- single-run polling ----------
  const stopRunPolling = useCallback(() => {
    if (runPollRef.current) {
      clearInterval(runPollRef.current);
      runPollRef.current = null;
    }
  }, []);

  const pollRun = useCallback(
    (runId) => {
      stopRunPolling();
      runPollRef.current = setInterval(async () => {
        try {
          const run = await getRun(runId);
          setActiveRun(run);

          if (run.company_id && run.status === "in_progress") {
            getCompany(run.company_id)
              .then((c) => setLiveStatus(c.status))
              .catch(() => {});
          }

          if (run.status !== "in_progress") {
            stopRunPolling();
            if (run.status === "completed") {
              toast.success("Workflow finished", { description: "Results are ready below." });
            } else {
              toast.error("Workflow failed", {
                description: run.error || "The workflow did not complete successfully.",
              });
            }
            // Let the page's own History section (a separate fetch, not
            // owned by this component) know a company just finished, so it
            // doesn't keep showing stale data until a manual page reload.
            onRunFinished?.();
          }
        } catch (err) {
          console.error("Run status poll failed:", err);
        }
      }, POLL_INTERVAL_MS);
    },
    [stopRunPolling]
  );

  // ---------- batch polling ----------
  const stopBatchPolling = useCallback(() => {
    if (batchPollRef.current) {
      clearInterval(batchPollRef.current);
      batchPollRef.current = null;
    }
  }, []);

  const refreshBatchCompanies = useCallback((batchId) => {
    getBatchCompanies(batchId)
      .then((data) => setBatchCompanies(data.items || []))
      .catch((err) => console.error("Failed to load batch companies:", err));
  }, []);

  const pollBatch = useCallback(
    (batchId) => {
      stopBatchPolling();
      batchPollRef.current = setInterval(async () => {
        try {
          const batch = await getBatch(batchId);
          setActiveBatch(batch);
          refreshBatchCompanies(batchId);

          if (batch.status !== "in_progress") {
            stopBatchPolling();
            // batch.status only means "every company finished", not "all
            // succeeded" - completed_count/failed_count is the real signal.
            if ((batch.failed_count || 0) === 0) {
              toast.success("Batch finished", {
                description: `${batch.completed_count} of ${batch.total_companies} companies processed successfully.`,
              });
            } else {
              toast.error("Batch finished with some failures", {
                description: `${batch.completed_count} succeeded, ${batch.failed_count} failed, out of ${batch.total_companies}.`,
              });
            }
            onRunFinished?.();
          }
        } catch (err) {
          console.error("Batch status poll failed:", err);
        }
      }, POLL_INTERVAL_MS);
    },
    [stopBatchPolling, refreshBatchCompanies]
  );

  // On mount: pick up any run AND any batch this user already has in
  // progress (survives a page refresh instead of only living in memory).
  useEffect(() => {
    (async () => {
      try {
        const run = await getActiveRun();
        setActiveRun(run);
        if (run?.status === "in_progress") pollRun(run.id);
      } catch (err) {
        console.error("Could not check for an active run:", err);
      } finally {
        setCheckingRunLock(false);
      }
    })();
    (async () => {
      try {
        const batch = await getActiveBatch();
        setActiveBatch(batch);
        if (batch?.status === "in_progress") {
          pollBatch(batch.id);
          refreshBatchCompanies(batch.id);
        }
      } catch (err) {
        console.error("Could not check for an active batch:", err);
      } finally {
        setCheckingBatchLock(false);
      }
    })();
    return () => {
      stopRunPolling();
      stopBatchPolling();
    };
  }, []);

  // Elapsed-time tickers - "time elapsed vs. the usual estimate", never a
  // countdown that would hit 0 and look broken on a slower-than-usual run.
  useEffect(() => {
    if (!isRunLocked || !activeRun?.started_at) {
      setRunElapsedSec(0);
      return;
    }
    const startedMs = new Date(activeRun.started_at).getTime();
    const tick = () => setRunElapsedSec(Math.max(0, Math.floor((Date.now() - startedMs) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [isRunLocked, activeRun?.started_at]);

  useEffect(() => {
    if (!isBatchLocked || !activeBatch?.started_at) {
      setBatchElapsedSec(0);
      return;
    }
    const startedMs = new Date(activeBatch.started_at).getTime();
    const tick = () => setBatchElapsedSec(Math.max(0, Math.floor((Date.now() - startedMs) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [isBatchLocked, activeBatch?.started_at]);

  // Once the current/last single run finishes successfully, load its company
  // record so the result view below the form has something to show.
  useEffect(() => {
    if (activeRun?.status === "completed" && activeRun?.company_id) {
      setLoadingResult(true);
      getCompany(activeRun.company_id)
        .then(setResultCompany)
        .catch((err) => console.error("Failed to load run result:", err))
        .finally(() => setLoadingResult(false));
    }
  }, [activeRun?.status, activeRun?.company_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !companyUrl.trim()) {
      toast.error("Missing details", { description: "Enter both a company name and URL." });
      return;
    }

    setSubmitting(true);
    try {
      const run = await triggerWorkflow({ companyName: name.trim(), companyUrl: companyUrl.trim() });
      setActiveRun(run);
      setResultCompany(null); // clear the previous run's results while this one processes
      setLiveStatus(null);
      pollRun(run.id);
      setName("");
      setCompanyUrl("");
      toast.success("Workflow started", { description: `Processing ${name.trim()}...` });
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("Something is already in progress", {
          description: err.response?.data?.detail || "Wait for it to finish before starting another.",
        });
        // Our local lock state was stale - resync both kinds.
        const run = await getActiveRun().catch(() => null);
        if (run) { setActiveRun(run); if (run.status === "in_progress") pollRun(run.id); }
        const batch = await getActiveBatch().catch(() => null);
        if (batch) { setActiveBatch(batch); if (batch.status === "in_progress") { pollBatch(batch.id); refreshBatchCompanies(batch.id); } }
      } else {
        toast.error("Could not start workflow", {
          description: err.response?.data?.detail || err.message,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // "Stop workflow" - see api.js's cancelRun docstring. Only shown/enabled
  // while a single-company run is actually locked (isRunLocked below).
  const handleCancelRun = async () => {
    if (!activeRun?.id) return;
    setCancellingRun(true);
    try {
      const cancelled = await cancelRun(activeRun.id);
      stopRunPolling();
      setActiveRun(cancelled);
      setLiveStatus(null);
      toast.success("Workflow stopped", { description: "You can start a new one now." });
    } catch (err) {
      console.error("Failed to cancel run", err);
      toast.error("Could not stop workflow", { description: err.response?.data?.detail || err.message });
    } finally {
      setCancellingRun(false);
    }
  };

  // ---------- CSV handling ----------
  const handleFileSelect = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Not a CSV file", { description: "Please choose a .csv file." });
      return;
    }
    setCsvFile(file);
  };

  const handleUploadBatch = async () => {
    if (!csvFile) return;
    setUploadingBatch(true);
    try {
      const batch = await uploadBatch(csvFile);
      setActiveBatch(batch);
      setBatchCompanies([]);
      pollBatch(batch.id);
      refreshBatchCompanies(batch.id);
      setCsvFile(null);
      toast.success("Batch started", { description: `Processing ${batch.total_companies} companies...` });
    } catch (err) {
      if (err.response?.status === 400) {
        // Per the ask: show the backend's detail string verbatim, in a
        // dialog - these messages (bad headers, too many rows) are meant to
        // be read carefully, not glanced at in a toast.
        setCsvErrorDialog(err.response?.data?.detail || "The uploaded CSV could not be processed.");
      } else if (err.response?.status === 409) {
        toast.error("Something is already in progress", {
          description: err.response?.data?.detail || "Wait for it to finish before starting another.",
        });
        const run = await getActiveRun().catch(() => null);
        if (run) { setActiveRun(run); if (run.status === "in_progress") pollRun(run.id); }
        const batch = await getActiveBatch().catch(() => null);
        if (batch) { setActiveBatch(batch); if (batch.status === "in_progress") { pollBatch(batch.id); refreshBatchCompanies(batch.id); } }
      } else {
        toast.error("Could not upload CSV", { description: err.response?.data?.detail || err.message });
      }
    } finally {
      setUploadingBatch(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files?.[0]);
  };

  const batchDone = activeBatch ? (activeBatch.completed_count || 0) + (activeBatch.failed_count || 0) : 0;
  const batchTotal = activeBatch?.total_companies || 0;
  const batchProgressPct = batchTotal > 0 ? Math.min(100, (batchDone / batchTotal) * 100) : 0;
  const batchEstimatedSeconds = activeBatch?.estimated_seconds ?? (batchTotal * ESTIMATED_SECONDS_PER_COMPANY);

  return (
    <section className="py-4">
      <div className="mx-auto max-w-4xl px-4">
        {/* Single vs. batch toggle */}
        <div className="mb-3 inline-flex rounded-xl border border-white/15 bg-white/[0.03] p-1">
          <button
            type="button"
            onClick={() => setMode("single")}
            className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${
              mode === "single" ? "bg-white/15 text-white" : "text-gray-400 hover:text-gray-200"
            }`}
          >
            Single company
          </button>
          <button
            type="button"
            onClick={() => setMode("batch")}
            className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${
              mode === "batch" ? "bg-white/15 text-white" : "text-gray-400 hover:text-gray-200"
            }`}
          >
            Multiple companies
          </button>
        </div>

        {mode === "single" ? (
          <>
            {isRunLocked && (
              <div className="mb-3 rounded-xl border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm text-blue-300">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-blue-400" />
                    Workflow in progress
                    {activeRun?.input?.Company_list?.[0]?.company_name
                      ? ` for ${activeRun.input.Company_list[0].company_name}`
                      : ""}
                    ... you can start another once this one finishes.
                  </div>
                  <span className="whitespace-nowrap text-xs text-blue-300/80">
                    {formatDuration(runElapsedSec)} elapsed · usually ~30-35s
                  </span>
                </div>
                <div className="mt-2 h-1 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-blue-400 transition-all duration-1000 ease-linear"
                    style={{ width: `${Math.min((runElapsedSec / ESTIMATED_SECONDS_PER_COMPANY) * 100, 95)}%` }}
                  />
                </div>
                {liveStatus && <StatusStepper status={liveStatus} className="mt-2.5" />}
              </div>
            )}
            {!isRunLocked && isBatchLocked && (
              <div className="mb-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-gray-400">
                A batch upload is in progress ({batchDone}/{batchTotal} done) — switch to "Multiple companies" to see it.
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-3 rounded-2xl border border-white/20 bg-transparent p-3 backdrop-blur-md sm:flex-row"
            >
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Enter Company name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isAnyLocked || checkingRunLock || checkingBatchLock}
                  className="w-full rounded-xl border border-gray-300/70 bg-transparent px-5 py-3.5 h-auto text-sm text-white outline-none transition-all placeholder:text-gray-500 focus-visible:ring-2 focus-visible:ring-blue-500/20"
                />
              </div>
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Enter company url"
                  value={companyUrl}
                  onChange={(e) => setCompanyUrl(e.target.value)}
                  disabled={isAnyLocked || checkingRunLock || checkingBatchLock}
                  className="w-full rounded-xl border border-gray-300/70 bg-transparent px-5 py-3.5 h-auto text-sm text-white outline-none transition-all placeholder:text-gray-500 focus-visible:ring-2 focus-visible:ring-blue-500/20"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting || isAnyLocked || checkingRunLock || checkingBatchLock}
                className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-7 py-3.5 h-auto text-sm font-semibold text-white shadow-lg shadow-blue-500/20 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-500/30 hover:from-blue-600 hover:to-indigo-600 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Starting..." : isRunLocked ? `In progress (${formatDuration(runElapsedSec)})...` : "Submit →"}
              </Button>
              {isRunLocked && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelRun}
                  disabled={cancellingRun}
                  className="rounded-xl border-red-500/30 bg-transparent px-7 py-3.5 h-auto text-sm font-semibold text-red-300 transition-all duration-300 hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancellingRun ? <Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> : null}
                  {cancellingRun ? "Stopping..." : "Stop workflow"}
                </Button>
              )}
            </form>

            {loadingResult ? (
              <div className="mt-4 flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-transparent py-8 text-sm text-gray-400 backdrop-blur-md">
                Loading results...
              </div>
            ) : (
              <CompanyResultView company={resultCompany} className="mt-4" />
            )}
          </>
        ) : (
          <>
            {isBatchLocked ? (
              <BatchProgressPanel
                batch={activeBatch}
                companies={batchCompanies}
                elapsedSec={batchElapsedSec}
                estimatedSeconds={batchEstimatedSeconds}
                donePct={batchProgressPct}
              />
            ) : (
              <>
                {isRunLocked && (
                  <div className="mb-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-gray-400">
                    A single-company workflow is in progress — switch to "Single company" to see it.
                  </div>
                )}

                <div
                  onClick={() => !isAnyLocked && fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); if (!isAnyLocked) setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={isAnyLocked ? undefined : handleDrop}
                  className={`flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${
                    isAnyLocked ? "cursor-not-allowed border-white/10 opacity-50" : "cursor-pointer border-white/20 hover:border-blue-500/40 hover:bg-blue-500/[0.03]"
                  } ${dragOver ? "border-blue-500/60 bg-blue-500/[0.06]" : ""}`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    disabled={isAnyLocked}
                    onChange={(e) => handleFileSelect(e.target.files?.[0])}
                  />
                  {csvFile ? (
                    <>
                      <FileSpreadsheet className="h-8 w-8 text-emerald-400" />
                      <div className="text-sm font-medium text-white">{csvFile.name}</div>
                      <div className="text-xs text-gray-500">{(csvFile.size / 1024).toFixed(1)} KB</div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setCsvFile(null); }}
                        className="mt-1 inline-flex items-center gap-1 text-xs text-gray-400 hover:text-red-300"
                      >
                        <X className="h-3 w-3" /> Remove
                      </button>
                    </>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-gray-500" />
                      <div className="text-sm text-gray-300">Drag & drop a CSV here, or click to browse</div>
                      <div className="text-xs text-gray-500">
                        Columns must be exactly <code className="text-gray-400">company_name</code>,{" "}
                        <code className="text-gray-400">website_url</code> — up to 500 companies.
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-3 flex justify-end">
                  <Button
                    type="button"
                    onClick={handleUploadBatch}
                    disabled={!csvFile || uploadingBatch || isAnyLocked}
                    className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-7 py-3.5 h-auto text-sm font-semibold text-white shadow-lg shadow-blue-500/20 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-500/30 hover:from-blue-600 hover:to-indigo-600 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploadingBatch ? <Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> : null}
                    {uploadingBatch ? "Uploading..." : "Upload & start →"}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* CSV validation error - shown verbatim per the user's explicit ask */}
      <Dialog open={!!csvErrorDialog} onOpenChange={(open) => { if (!open) setCsvErrorDialog(null); }}>
        <DialogContent className="max-w-md border-white/20 bg-[#0b0b0f] text-white">
          <DialogHeader>
            <DialogTitle className="text-white">CSV upload failed</DialogTitle>
          </DialogHeader>
          <p className="whitespace-pre-wrap text-sm text-gray-300">{csvErrorDialog}</p>
          <div className="flex justify-end">
            <Button onClick={() => setCsvErrorDialog(null)} className="rounded-full bg-indigo-500 hover:bg-indigo-400">
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

// Live progress panel for an in-progress batch: a real fill-based progress
// bar (we know exactly how many of total_companies are done, unlike a
// single run) plus the same elapsed-vs-estimate framing, and a per-company
// breakdown table so the user can see which companies are still going,
// which finished, and which failed (with why).
function BatchProgressPanel({ batch, companies, elapsedSec, estimatedSeconds, donePct }) {
  return (
    <div className="rounded-2xl border border-blue-500/30 bg-blue-500/[0.04] p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-blue-300">
          <span className="h-2 w-2 animate-pulse rounded-full bg-blue-400" />
          Batch in progress — {batch.completed_count || 0} done, {batch.failed_count || 0} failed, {batch.total_companies} total
        </div>
        <span className="whitespace-nowrap text-xs text-blue-300/80">
          {formatDuration(elapsedSec)} elapsed · usually ~{formatDuration(estimatedSeconds)} for this batch
        </span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
        <div className="h-full rounded-full bg-blue-400 transition-all duration-1000 ease-linear" style={{ width: `${donePct}%` }} />
      </div>

      <div className="mt-4 max-h-80 overflow-y-auto rounded-xl border border-white/10">
        <table className="w-full text-left text-sm text-gray-200">
          <thead className="sticky top-0 bg-[#0b0b0f]">
            <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-gray-400">
              <th className="px-3 py-2 font-medium">Company</th>
              <th className="px-3 py-2 font-medium">Run status</th>
              <th className="px-3 py-2 font-medium">Pipeline stage</th>
            </tr>
          </thead>
          <tbody>
            {companies.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-3 py-6 text-center text-gray-500">Loading breakdown...</td>
              </tr>
            ) : (
              companies.map((c) => (
                <tr key={c.run_id} className="border-b border-white/5 last:border-0">
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-white">{c.company_name || "—"}</div>
                    <div className="text-xs text-gray-500">{c.website_url}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <RunStatusChip status={c.status} error={c.error} />
                  </td>
                  <td className="px-3 py-2.5">
                    {c.company_status ? <StatusStepper status={c.company_status} /> : <span className="text-xs text-gray-500">Not started yet</span>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RunStatusChip({ status, error }) {
  if (status === "completed") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-300">
        <CheckCircle2 className="h-3.5 w-3.5" /> Completed
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-300" title={error || undefined}>
        <XCircle className="h-3.5 w-3.5" /> Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-300">
      <Clock3 className="h-3.5 w-3.5 animate-pulse" /> In progress
    </span>
  );
}

export default Startworlflow;
